/**
 * Global axios configuration with silent session refresh.
 *
 * The backend issues a short-lived access_token (15 min) and a longer-lived
 * refresh_token (7 days), both as httpOnly cookies. When the access token
 * expires the server responds 401. This module wires a one-shot retry that:
 *   1. Sees a 401 from any /api/* call
 *   2. Calls POST /api/auth/refresh once (refresh cookie is sent automatically)
 *   3. On success — replays the original request transparently
 *   4. On failure — fires a single `auth:session-expired` window event so
 *      AuthContext can clear state, toast the user, and redirect to /login
 *
 * Concurrent 401s are de-duped onto a single in-flight refresh promise so a
 * page that fires five parallel requests after waking from sleep only does
 * ONE refresh call.
 */
import axios from 'axios';
import { BACKEND_URL } from './api';

// Send cookies on every request by default.
axios.defaults.withCredentials = true;

// Endpoints that must NEVER trigger a refresh attempt — to avoid loops.
const AUTH_BYPASS = [
  '/api/auth/login',
  '/api/auth/register',
  '/api/auth/refresh',
  '/api/auth/logout',
  '/api/auth/forgot-password',
  '/api/auth/reset-password',
];

const isAuthBypass = (url = '') => AUTH_BYPASS.some((p) => url.includes(p));

let refreshPromise = null;

const performRefresh = () => {
  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${BACKEND_URL}/api/auth/refresh`, {}, { withCredentials: true, _skipAuthInterceptor: true })
      .finally(() => {
        // Allow the next 401 cycle to start a fresh refresh attempt.
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

let sessionExpiredFired = false;

const fireSessionExpired = () => {
  if (sessionExpiredFired) return;
  // Only fire if the user was actually authenticated at some point in this
  // browser. Anonymous visitors hitting public pages also trigger a 401 on
  // /api/auth/me at boot — that is NOT "session expired", it's just "not
  // logged in". AuthContext sets this flag on successful checkAuth/login.
  try {
    if (window.localStorage.getItem('vinepass:was-authed') !== '1') return;
  } catch { /* localStorage blocked — fall through and fire */ }
  sessionExpiredFired = true;
  // Reset the latch on the next tick so a future login → expire cycle works.
  setTimeout(() => {
    sessionExpiredFired = false;
  }, 2000);
  try {
    window.dispatchEvent(new CustomEvent('auth:session-expired'));
  } catch {
    // SSR / no-window environments — nothing to do.
  }
};

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;

    // Only act on 401s from non-auth endpoints that we haven't already retried.
    if (
      status !== 401 ||
      original._retry ||
      original._skipAuthInterceptor ||
      isAuthBypass(original.url || '')
    ) {
      return Promise.reject(error);
    }

    original._retry = true;

    try {
      await performRefresh();
    } catch (refreshErr) {
      // Refresh failed — the user really is signed out. Tell the app once,
      // then surface the ORIGINAL error so individual callers can still log.
      fireSessionExpired();
      return Promise.reject(error);
    }

    // Refresh worked — replay the original request with the new cookie.
    return axios({ ...original, _skipAuthInterceptor: false });
  },
);

export default axios;
